import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Package, Plus, Trash2, Loader2, ExternalLink, Send } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const emptyItem = () => ({ description: "", quantity: 1, unit: "unit", estimated_price: 0, notes: "", link: "" });

export const MaterialRequestButton = ({ ticketId }) => {
  const { t } = useLang();
  const { user } = useAuth();
  // Only Purchasing/Admin/Manager see and enter prices. Techs and PMs skip pricing.
  const showPrice = ["purchasing", "admin", "manager"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [photos, setPhotos] = useState([]);

  const load = async () => {
    try { setExisting(await api.get(`/material-requests?ticket_id=${ticketId}`)); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const total = items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.estimated_price) || 0), 0);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.length > 1 ? prev.filter((_, j) => j !== idx) : prev);
  const patchItem = (idx, patch) => setItems((prev) => prev.map((x, j) => j === idx ? { ...x, ...patch } : x));

  const submit = async () => {
    const clean = items.filter((i) => (i.description || "").trim());
    if (clean.length === 0) { toast.error(t("mrNeedItem") || "Add at least one item"); return; }
    setBusy(true);
    try {
      await api.post("/material-requests", {
        ticket_id: ticketId, notes, urgency, photos,
        items: clean.map((i) => ({
          description: i.description.trim(),
          quantity: parseFloat(i.quantity) || 1,
          unit: i.unit || "unit",
          estimated_price: showPrice ? (parseFloat(i.estimated_price) || 0) : 0,
          notes: i.notes || "",
          link: (i.link || "").trim(),
        })),
      });
      toast.success(t("saved"));
      setOpen(false);
      setItems([emptyItem()]); setNotes(""); setUrgency("normal"); setPhotos([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    const reader = new FileReader();
    reader.onload = () => setPhotos((p) => [...p, reader.result]);
    reader.readAsDataURL(file);
  };

  return (
    <div className="border-t border-slate-200 pt-4 space-y-2" data-testid="mr-block">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("materialRequests") || "Material Requests"}</span>
          {existing.length > 0 && <span className="text-xs text-slate-400">({existing.length})</span>}
        </div>
        <button data-testid="mr-open-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg px-2.5 py-1.5 hover:bg-blue-50"><Plus className="w-3.5 h-3.5" /> {t("requestMaterial") || "Request Material"}</button>
      </div>

      {existing.length > 0 && (
        <ul className="space-y-1">
          {existing.map((r) => (
            <li key={r.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs">
              <span className="truncate"><span className="font-semibold text-slate-800">{r.number}</span> <span className="text-slate-500">— {r.items.length} items • {r.status.replace(/_/g, " ")}</span></span>
              {showPrice && <span className="font-semibold tabular-nums text-slate-700">{money(r.actual_total ?? r.estimated_total)}</span>}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{t("newMaterialRequest") || "New Material Request"}</h2>
                {!showPrice && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {t("mrTechHint") || "Add each item you need — Purchasing will handle the pricing."}
                  </p>
                )}
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">{t("urgency") || "Urgency"}</label>
                <select data-testid="mr-urgency" value={urgency} onChange={(e) => setUrgency(e.target.value)} className={inputCls}>
                  <option value="normal">{t("normal") || "Normal"}</option>
                  <option value="urgent">{t("urgent") || "Urgent"}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">{t("notes") || "Notes"}</label>
                <textarea data-testid="mr-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} placeholder={t("mrNotesPlaceholder") || "Describe the situation for the purchasing team..."} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold uppercase text-slate-500">{t("photos") || "Photos"}</label>
                  <label className="text-xs text-blue-600 cursor-pointer hover:underline">
                    + {t("addPhoto") || "Add photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden">
                      <img src={p} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs">×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase text-slate-500">{t("items") || "Items"}</label>
                  <button data-testid="mr-add-item" onClick={addItem} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">
                    <Plus className="w-3.5 h-3.5" /> {t("addItem") || "Add item"}
                  </button>
                </div>
                <div className="space-y-3">
                  {items.map((it, i) => (
                    <div key={i} data-testid={`mr-item-${i}`} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">#{i + 1}</span>
                        <button
                          data-testid={`mr-item-remove-${i}`}
                          onClick={() => removeItem(i)}
                          disabled={items.length === 1}
                          className="p-1 rounded text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400"
                          title={t("remove") || "Remove"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className={`grid gap-2 ${showPrice ? "grid-cols-12" : "grid-cols-6"}`}>
                        <input
                          data-testid={`mr-item-desc-${i}`}
                          placeholder={t("materialName") || "Material name / description"}
                          value={it.description}
                          onChange={(e) => patchItem(i, { description: e.target.value })}
                          className={`${inputCls} ${showPrice ? "col-span-7" : "col-span-4"}`}
                        />
                        <input
                          data-testid={`mr-item-qty-${i}`}
                          type="number"
                          placeholder="Qty"
                          value={it.quantity}
                          onChange={(e) => patchItem(i, { quantity: e.target.value })}
                          className={`${inputCls} ${showPrice ? "col-span-2" : "col-span-1"} text-right`}
                          step="0.01"
                          min="0"
                        />
                        <input
                          data-testid={`mr-item-unit-${i}`}
                          placeholder="Unit"
                          value={it.unit}
                          onChange={(e) => patchItem(i, { unit: e.target.value })}
                          className={`${inputCls} ${showPrice ? "col-span-3" : "col-span-1"}`}
                        />
                        {showPrice && (
                          <input
                            data-testid={`mr-item-price-${i}`}
                            type="number"
                            placeholder="$ Est."
                            value={it.estimated_price}
                            onChange={(e) => patchItem(i, { estimated_price: e.target.value })}
                            className={`${inputCls} col-span-12 sm:col-span-12 text-right`}
                            step="0.01"
                            min="0"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <input
                          data-testid={`mr-item-link-${i}`}
                          placeholder={t("itemLinkPlaceholder") || "URL / photo link for this item (optional)"}
                          value={it.link}
                          onChange={(e) => patchItem(i, { link: e.target.value })}
                          className={`${inputCls} text-xs`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {showPrice && (
                  <div className="mt-3 flex items-center justify-end gap-3 text-sm">
                    <span className="text-xs font-bold uppercase text-slate-400">{t("estimatedTotal") || "Estimated total"}</span>
                    <span className="text-lg font-bold tabular-nums text-slate-900" data-testid="mr-total">{money(total)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50 disabled:opacity-40">{t("cancel")}</button>
              <button data-testid="mr-submit" onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("submit") || "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialRequestButton;
